import React from 'react';
import { useVendorDashboard } from '../../hooks/useDashboard';
import { KPICard } from '../../components/dashboard/KPICard';
import { Card } from '../../components/ui/Card';
import { FilterBar } from '../../components/dashboard/FilterBar';
import { DashboardGrid } from '../../components/dashboard/DashboardGrid';
import { ExportButton } from '../../components/dashboard/ExportButton';
import { Star, Clock, CheckCircle2, IndianRupee } from 'lucide-react';
import { LoadingSpinner } from '../../components/ui/Feedback';
import { formatCurrency } from '../../services/reports/excelExport';

export const VendorDashboardPage: React.FC = () => {
  const vendorId = 'VND-001';
  const { data, loading, error, filter, setFilter } = useVendorDashboard(vendorId);

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-rose-400 text-sm p-4">{error}</div>;
  if (!data) return null;

  const performance = data.performance;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <FilterBar filter={filter} onChange={setFilter} />
        <ExportButton
          config={{
            filename: 'vendor-dashboard',
            sheetName: 'Vendor Dashboard',
            columns: [
              { key: 'label', header: 'Metric', width: 25 },
              { key: 'value', header: 'Value', width: 15 },
            ],
            data: data.kpis,
            filters: filter,
            generatedAt: new Date().toISOString(),
            generatedBy: 'Vendor',
          }}
        />
      </div>

      <DashboardGrid columns={4}>
        {data.kpis.map((kpi, i) => (
          <KPICard key={i} metric={kpi} />
        ))}
      </DashboardGrid>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Performance Metrics" subtitle="Last 30 days">
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-900/60 border border-slate-800 rounded-lg">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-sky-400" />
                <span className="text-sm text-slate-300">Avg Response Time</span>
              </div>
              <span className="text-sm font-semibold text-sky-400">{performance.avg_response_time}h</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-900/60 border border-slate-800 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-slate-300">On-Time Completion</span>
              </div>
              <span className="text-sm font-semibold text-emerald-400">{performance.on_time_completion}%</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-900/60 border border-slate-800 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-violet-400" />
                <span className="text-sm text-slate-300">Inspection Pass Rate</span>
              </div>
              <span className="text-sm font-semibold text-violet-400">{performance.inspection_pass_rate}%</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-900/60 border border-slate-800 rounded-lg">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-400" />
                <span className="text-sm text-slate-300">Rating</span>
              </div>
              <span className="text-sm font-semibold text-amber-400">{performance.rating}/5</span>
            </div>
          </div>
        </Card>

        <Card title="Monthly Billing" subtitle="Last 3 months">
          <div className="space-y-3">
            {data.monthly_billing.map((month, i) => (
              <div key={i} className="p-3 bg-slate-900/60 border border-slate-800 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-200">{month.month}</span>
                  <span className="text-sm font-semibold text-sky-400">{formatCurrency(month.total_billed)}</span>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-emerald-400">Paid: {formatCurrency(month.total_paid)}</span>
                  <span className="text-amber-400">Pending: {formatCurrency(month.pending_amount)}</span>
                </div>
                <div className="mt-2 w-full bg-slate-800 rounded-full h-2">
                  <div
                    className="bg-emerald-500 h-2 rounded-full"
                    style={{ width: `${(month.total_paid / month.total_billed) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};
